import { type BaseAnalyticsEvent } from './events'

export const LOGIN_TYPE = {
  EMAIL_OTP: 'email_otp',
  EMAIL_PW: 'email_pw',
  MOBILE_OTP: 'mobile_otp',
  MOBILE_PW: 'mobile_pw',
  LINE: 'line',
  FACEBOOK: 'facebook',
  GMAIL: 'gmail',
  GOOGLE: 'google',
} as const

export type LoginType = typeof LOGIN_TYPE[keyof typeof LOGIN_TYPE]

export interface ViewLoginPageEvent extends BaseAnalyticsEvent {
  eventName: 'view login page'
  eventParams: {
    page_title: string
    page_location: string
  }
}
interface BaseLoginSignupEvent extends BaseAnalyticsEvent {
  eventParams: {
    event_category: 'Login' | 'Signup'
  }
}

export interface LoginMethodEvent extends BaseLoginSignupEvent {
  eventName: 'login method'
  eventParams: {
    event_category: 'Login' | 'Signup'
    account_type: LoginType
  }
}

export interface LoginCountryCodeEvent extends BaseLoginSignupEvent {
  eventName: 'country code'
  eventParams: {
    event_category: 'Login'
    country_code: string
  }
}

export interface LoginSearchCountryCodeEvent extends BaseLoginSignupEvent {
  eventName: 'country code search'
  eventParams: {
    event_category: 'Login'
    search_result: 'no result'
  }
}

export interface LoginFailedEvent extends BaseLoginSignupEvent {
  eventName: 'login failed'
  eventParams: {
    event_category: 'Login'
    login_error: 'empty error' | 'incorrect format' | 'server error' | 'thirty party login error'
  }
}

interface MobileOTPEvent extends BaseAnalyticsEvent {
  eventParams: {
    event_category: 'Mobile OTP'
  }
}

export interface MobileOTPStartedEvent extends MobileOTPEvent {
  eventName: 'mobile OTP started'
}

export interface MobileOTPReceiveEvent extends MobileOTPEvent {
  eventName: 'mobile OTP'
  eventParams: {
    event_category: 'Mobile OTP'
    mobile_otp_receive_result: boolean
  }
}

export interface MobileOTPErrorEvent extends MobileOTPEvent {
  eventName: 'mobile OTP error'
  eventParams: {
    event_category: 'Mobile OTP'
    mobile_otp_error: 'expired' | 'incorrect'
  }
}

export interface MobileOTPPwLoginEvent extends MobileOTPEvent {
  eventName: 'mobile OTP pw login'
}

export interface MobileOTPBackToLoginEvent extends MobileOTPEvent {
  eventName: 'mobile OTP back to login'
}

export interface MobileOTPDropEvent extends MobileOTPEvent {
  eventName: 'mobile OTP drop'
}

export interface MobileOTPResendEvent extends MobileOTPEvent {
  eventName: 'mobile OTP resend'
  eventParams: {
    event_category: 'Mobile OTP'
    mobile_otp_resend_clicks: number
  }
}

export interface MobileOTPResendLimitReachedEvent extends MobileOTPEvent {
  eventName: 'mobile OTP resend limit reached'
}

export interface MobileOTPCsEvent extends MobileOTPEvent {
  eventName: 'mobile OTP cs'
}

export interface MobileOTPReturnEvent extends MobileOTPEvent {
  eventName: 'mobile OTP return'
}

interface EmailLoginEvent extends BaseAnalyticsEvent {
  eventParams: {
    event_category: 'Email login'
  }
}

export interface EmailDropEvent extends EmailLoginEvent {
  eventName: 'Email drop'
}

export interface EmailVerifyEvent extends EmailLoginEvent {
  eventName: 'Email verify'
  eventParams: {
    event_category: 'Email login'
    email_login_verify: boolean
  }
}

export interface EmailLoginErrorEvent extends EmailLoginEvent {
  eventName: 'Email login error'
  eventParams: {
    event_category: 'Email login'
    email_login_error: 'incorrect format' | 'empty'
  }
}

export interface EmailReturnEvent extends EmailLoginEvent {
  eventName: 'Email return'
}

export interface EmailLoginCsEvent extends EmailLoginEvent {
  eventName: 'Email login cs'
}

interface EmailOTPEvent extends BaseAnalyticsEvent {
  eventParams: {
    event_category: 'Email OTP'
  }
}

export interface EmailOTPStartedEvent extends EmailOTPEvent {
  eventName: 'Email OTP started'
}

export interface EmailOTPReceiveEvent extends EmailOTPEvent {
  eventName: 'Email OTP'
  eventParams: {
    event_category: 'Email OTP'
    email_otp_receive_result: boolean
  }
}

export interface EmailOTPErrorEvent extends EmailOTPEvent {
  eventName: 'Email OTP error'
  eventParams: {
    event_category: 'Email OTP'
    email_otp_error: 'expired' | 'incorrect'
  }
}

export interface EmailOTPPwLoginEvent extends EmailOTPEvent {
  eventName: 'Email OTP pw login'
}

export interface EmailOTPBackToLoginEvent extends EmailOTPEvent {
  eventName: 'Email OTP back to login'
}

export interface EmailOTPDropEvent extends EmailOTPEvent {
  eventName: 'Email OTP drop'
}

export interface EmailOTPResendEvent extends EmailOTPEvent {
  eventName: 'Email OTP resend'
  eventParams: {
    event_category: 'Email OTP'
    email_otp_resend_clicks: number
  }
}

export interface EmailOTPResendLimitReachedEvent extends EmailOTPEvent {
  eventName: 'Email OTP resend limit reached'
}

export interface EmailOTPCsEvent extends EmailOTPEvent {
  eventName: 'Email OTP cs'
}

export interface EmailOTPReturnEvent extends EmailOTPEvent {
  eventName: 'Email OTP return'
}

interface EmailTokenLoadingEvent extends BaseAnalyticsEvent {
  eventParams: {
    event_category: 'Email token loading'
  }
}

export interface EmailTokenVerifyEvent extends EmailTokenLoadingEvent {
  eventName: 'Email verify token'
}

export interface EmailTokenErrorEvent extends EmailTokenLoadingEvent {
  eventName: 'Email verify token error'
  eventParams: {
    event_category: 'Email token loading'
    email_verify_token_error: 'expired' | 'server error'
  }
}

export interface EmailTokenBackToLoginEvent extends EmailTokenLoadingEvent {
  eventName: 'Email verify token back to login'
  eventParams: {
    event_category: 'Email token loading'
    email_verify_token_error: 'expired' | 'server error'
  }
}

export interface EmailTokenDropEvent extends EmailTokenLoadingEvent {
  eventName: 'Email verify token drop'
}

export interface ForgetPasswordEvent extends BaseAnalyticsEvent {
  eventName: 'Forget password'
  eventParams: {
    event_category: 'Password'
  }
}

export type CosignEvents =
  | LoginMethodEvent
  | LoginCountryCodeEvent
  | LoginSearchCountryCodeEvent
  | LoginFailedEvent
  | MobileOTPStartedEvent
  | MobileOTPReceiveEvent
  | MobileOTPErrorEvent
  | MobileOTPPwLoginEvent
  | MobileOTPBackToLoginEvent
  | MobileOTPDropEvent
  | MobileOTPResendEvent
  | MobileOTPResendLimitReachedEvent
  | MobileOTPCsEvent
  | MobileOTPReturnEvent
  | EmailDropEvent
  | EmailVerifyEvent
  | EmailLoginErrorEvent
  | EmailReturnEvent
  | EmailLoginCsEvent
  | EmailOTPStartedEvent
  | EmailOTPReceiveEvent
  | EmailOTPErrorEvent
  | EmailOTPPwLoginEvent
  | EmailOTPBackToLoginEvent
  | EmailOTPDropEvent
  | EmailOTPResendEvent
  | EmailOTPResendLimitReachedEvent
  | EmailOTPCsEvent
  | EmailOTPReturnEvent
  | EmailTokenVerifyEvent
  | EmailTokenErrorEvent
  | EmailTokenBackToLoginEvent
  | EmailTokenDropEvent
  | ViewLoginPageEvent
  | ForgetPasswordEvent
